// src/components/AddAttendanceAdminModal.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react/dist/iconify.js";
import {
  getMyFacilities,
  getAssignedUsers,
} from "../redux/slices/facilitySlice";
import { addAttendanceAdmin } from "../redux/slices/dailyAttendanceSlice";

const AddAttendanceAdminModal = ({ onClose, onSave, isSaving }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    facility_id: "",
    user_id: "",
    startDateTime: "",
    endDateTime: "",
  });
  const [dateError, setDateError] = useState("");
  const [durationInfo, setDurationInfo] = useState({
    text: "",
    isValid: true,
    isOverMax: false,
    isUnderMin: false,
    isZero: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const { myFacilities, isFetching: isLoadingFacilities } = useSelector(
    (state) => state.facility,
  );
  const { assignedUsers, isFetchingAssignedUsers } = useSelector(
    (state) => state.facility,
  );

  // Fetch facilities on mount
  useEffect(() => {
    dispatch(getMyFacilities(1));
  }, [dispatch]);

  // Fetch users when facility changes
  useEffect(() => {
    if (formData.facility_id) {
      setIsLoadingUsers(true);
      dispatch(
        getAssignedUsers({ facilityId: formData.facility_id, page: 1 }),
      ).finally(() => setIsLoadingUsers(false));
    }
  }, [formData.facility_id, dispatch]);

  const getFacilitiesList = () => {
    if (myFacilities?.data?.data) {
      return myFacilities.data.data;
    }
    if (Array.isArray(myFacilities?.data)) {
      return myFacilities.data;
    }
    if (Array.isArray(myFacilities)) {
      return myFacilities;
    }
    return [];
  };

  const getUsersList = () => {
    if (assignedUsers?.data?.data) {
      return assignedUsers.data.data;
    }
    if (Array.isArray(assignedUsers?.data)) {
      return assignedUsers.data;
    }
    if (Array.isArray(assignedUsers)) {
      return assignedUsers;
    }
    return [];
  };

  const facilities = getFacilitiesList();
  const users = getUsersList();

  const calculateDuration = (start, end) => {
    if (!start || !end) return null;

    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffHours = (endDate - startDate) / (1000 * 60 * 60);

    if (diffHours <= 0) return null;

    const hours = Math.floor(diffHours);
    const minutes = Math.floor((diffHours - hours) * 60);

    return {
      hours,
      minutes,
      totalHours: diffHours,
      formatted: `${hours}h ${minutes}m`,
    };
  };

  const validateDuration = (duration) => {
    if (!duration)
      return {
        isValid: true,
        isOverMax: false,
        isUnderMin: false,
        isZero: false,
      };

    // COMMENTED OUT: Client may want to revert these duration limits
    // const isOverMax = duration.totalHours > 24;
    // const isUnderMin = duration.totalHours < 0.5 && duration.totalHours > 0;
    const isOverMax = false;
    const isUnderMin = false;
    const isZero = duration.totalHours === 0;
    // const isValid = !isOverMax && !isUnderMin && !isZero;
    const isValid = !isZero;

    return { isValid, isOverMax, isUnderMin, isZero };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: value,
      };

      // Calculate and validate duration when both dates are set
      if (newData.startDateTime && newData.endDateTime) {
        const startDate = new Date(newData.startDateTime);
        const endDate = new Date(newData.endDateTime);

        // Check if dates are the same
        if (startDate.getTime() === endDate.getTime()) {
          setDurationInfo({
            text: "0h 0m",
            isValid: false,
            isOverMax: false,
            isUnderMin: false,
            isZero: true,
          });
        } else {
          const duration = calculateDuration(
            newData.startDateTime,
            newData.endDateTime,
          );
          if (duration) {
            const validation = validateDuration(duration);
            setDurationInfo({
              text: duration.formatted,
              ...validation,
            });
          } else {
            setDurationInfo({
              text: "",
              isValid: true,
              isOverMax: false,
              isUnderMin: false,
              isZero: false,
            });
          }
        }
      } else {
        setDurationInfo({
          text: "",
          isValid: true,
          isOverMax: false,
          isUnderMin: false,
          isZero: false,
        });
      }

      return newData;
    });

    if (name === "startDateTime" || name === "endDateTime") {
      setDateError("");
    }
  };

  const validateDates = () => {
    const startDate = new Date(formData.startDateTime);
    const endDate = new Date(formData.endDateTime);

    // Remove future date validation for admin flow
    if (endDate < startDate) {
      setDateError("End date cannot be before start date");
      return false;
    }

    // Check if dates are the same (zero duration)
    // if (startDate.getTime() === endDate.getTime()) {
    //   setDateError("Start and end times cannot be the same");
    //   return false;
    // }

    // Check duration limits
    // if (durationInfo.isOverMax) {
    //   setDateError("Attendance period cannot exceed 24 hours");
    //   return false;
    // }

    // if (durationInfo.isUnderMin) {
    //   setDateError("Attendance period must be at least 30 minutes");
    //   return false;
    // }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.facility_id) {
      setDateError("Please select a facility");
      return;
    }
    if (!formData.user_id) {
      setDateError("Please select a user");
      return;
    }

    if (!validateDates()) {
      return;
    }

    setIsSubmitting(true);
    setDateError(null);

    // Send raw datetime strings to thunk for UTC conversion
    const attendanceData = {
      start_time: formData.startDateTime,
      end_time: formData.endDateTime,
      facility_id: parseInt(formData.facility_id),
      user_id: parseInt(formData.user_id),
    };

    try {
      const result = await dispatch(
        addAttendanceAdmin(attendanceData),
      ).unwrap();

      if (result.success) {
        if (onSave) {
          await onSave(attendanceData);
        }
        onClose();
      } else {
        setDateError(result.message || "Failed to create attendance");
      }
    } catch (err) {
      console.error("Error creating attendance:", err);
      setDateError(
        err.message || "An error occurred while creating the attendance",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #E0E0E0",
    fontSize: "14px",
    backgroundColor: "#fff",
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer",
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    backgroundSize: "16px",
  };

  const errorStyle = {
    color: "#D32F2F",
    fontSize: "12px",
    marginTop: "5px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  };

  const getDurationColor = () => {
    if (durationInfo.isZero) return "#D32F2F";
    if (durationInfo.isOverMax || durationInfo.isUnderMin) return "#D32F2F";
    return "#28a745";
  };

  const isFormValid = () => {
    return (
      formData.facility_id &&
      formData.user_id &&
      formData.startDateTime &&
      formData.endDateTime &&
      durationInfo.isValid &&
      !durationInfo.isZero
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ padding: "20px" }}>
        {/* Facility Dropdown */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
              fontSize: "14px",
              color: "#333",
            }}>
            Facility <span style={{ color: "#D32F2F" }}>*</span>
          </label>
          <select
            name="facility_id"
            value={formData.facility_id}
            onChange={handleChange}
            required
            disabled={isLoadingFacilities || isSubmitting || isSaving}
            style={selectStyle}>
            <option value="">
              {isLoadingFacilities
                ? "Loading facilities..."
                : "Select a facility"}
            </option>
            {facilities.map((facility) => (
              <option key={facility.id} value={facility.id}>
                {facility.facility_name}
              </option>
            ))}
          </select>
        </div>

        {/* User Dropdown */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
              fontSize: "14px",
              color: "#333",
            }}>
            User <span style={{ color: "#D32F2F" }}>*</span>
          </label>
          <select
            name="user_id"
            value={formData.user_id}
            onChange={handleChange}
            required
            disabled={
              !formData.facility_id ||
              isLoadingUsers ||
              isSubmitting ||
              isSaving
            }
            style={selectStyle}>
            <option value="">
              {!formData.facility_id
                ? "Select facility first"
                : isLoadingUsers
                  ? "Loading users..."
                  : "Select a user"}
            </option>
            {users.map((user) => (
              <option
                key={user.user_id || user.id}
                value={user.user_id || user.id}>
                {user.name || user.user_name}{" "}
                {user.email ? `(${user.email})` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Start Date & Time */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
              fontSize: "14px",
              color: "#333",
            }}>
            Start Date & Time <span style={{ color: "#D32F2F" }}>*</span>
          </label>
          <input
            type="datetime-local"
            name="startDateTime"
            value={formData.startDateTime}
            onChange={handleChange}
            required
            disabled={isSubmitting || isSaving}
            style={{
              ...inputStyle,
              borderColor: dateError ? "#D32F2F" : "#E0E0E0",
            }}
          />
        </div>

        {/* End Date & Time */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontWeight: "500",
              fontSize: "14px",
              color: "#333",
            }}>
            End Date & Time <span style={{ color: "#D32F2F" }}>*</span>
          </label>
          <input
            type="datetime-local"
            name="endDateTime"
            value={formData.endDateTime}
            onChange={handleChange}
            required
            disabled={isSubmitting || isSaving}
            style={{
              ...inputStyle,
              borderColor: dateError ? "#D32F2F" : "#E0E0E0",
            }}
          />

          {/* Duration display with color coding */}
          {durationInfo.text && (
            <div
              style={{
                fontSize: "13px",
                color: getDurationColor(),
                marginTop: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontWeight:
                  durationInfo.isZero ||
                  durationInfo.isOverMax ||
                  durationInfo.isUnderMin
                    ? "500"
                    : "400",
                padding: "4px 8px",
                backgroundColor:
                  durationInfo.isZero ||
                  durationInfo.isOverMax ||
                  durationInfo.isUnderMin
                    ? "#ffebee"
                    : "#f0f9f0",
                borderRadius: "4px",
              }}>
              <Icon
                icon={
                  durationInfo.isZero ||
                  durationInfo.isOverMax ||
                  durationInfo.isUnderMin
                    ? "mdi:alert-circle"
                    : "mdi:clock-check"
                }
                width="16"
                height="16"
              />
              <span>
                Duration: {durationInfo.text}
                {durationInfo.isZero &&
                  " (Start and end times cannot be the same)"}
                {durationInfo.isOverMax && " (Exceeds 24h limit)"}
                {durationInfo.isUnderMin && " (Below 30min minimum)"}
              </span>
            </div>
          )}

          {/* Time limit helper text */}
          <div
            style={{
              display: "flex",
              gap: "16px",
              marginTop: "12px",
              fontSize: "11px",
              color: "#666",
              borderTop: "1px dashed #E0E0E0",
              paddingTop: "8px",
            }}>
            {/* <span>• Max: 24 hours</span> */}
            {/* <span>• Min: 30 minutes</span> */}
            <span>• Cannot be same time</span>
          </div>
        </div>

        {dateError && (
          <div style={errorStyle}>
            <Icon icon="mdi:alert-circle" width="16" height="16" />
            {dateError}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "30px",
          }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting || isSaving}
            style={{
              padding: "8px 16px",
              border: "1px solid #E0E0E0",
              backgroundColor: "white",
              color: "#333",
              borderRadius: "6px",
              cursor: isSubmitting || isSaving ? "not-allowed" : "pointer",
              fontSize: "14px",
            }}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              isSubmitting ||
              isSaving ||
              !isFormValid() ||
              isLoadingFacilities ||
              isLoadingUsers
            }
            style={{
              padding: "8px 16px",
              border: "none",
              backgroundColor:
                isSubmitting ||
                isSaving ||
                !isFormValid() ||
                isLoadingFacilities ||
                isLoadingUsers
                  ? "#cccccc"
                  : "#8B2885",
              color: "white",
              borderRadius: "6px",
              cursor:
                isSubmitting ||
                isSaving ||
                !isFormValid() ||
                isLoadingFacilities ||
                isLoadingUsers
                  ? "not-allowed"
                  : "pointer",
              fontSize: "14px",
              opacity:
                isSubmitting ||
                isSaving ||
                !isFormValid() ||
                isLoadingFacilities ||
                isLoadingUsers
                  ? 0.7
                  : 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}>
            {isSubmitting || isSaving ? (
              <>
                <Icon
                  icon="mdi:loading"
                  style={{ animation: "spin 1s linear infinite" }}
                />
                Creating...
              </>
            ) : (
              "Create Attendance"
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </form>
  );
};

export default AddAttendanceAdminModal;
